import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Page, alert } from 'equasion-lib';
import { FiChevronLeft, FiChevronRight, FiEye } from 'react-icons/fi';
import { readScannedLot, scannedLotSearch } from '@/lib/scanning/scannedLot';
import ScannedDetails from './com/ScannedDetails';
import './ScannedOptions.scss';

const statusToActionNamesMap = {
  CREATED: ['Receive'],
  RECEIVED: ['Stock', 'Bulk Stock', 'Quarantine', 'Return to Vendor'],
  STOCKED: ['Move', 'Bulk Move', 'Quarantine', 'Return to Vendor', 'Destroy'],
  QUARANTINED: ['Stock', 'Move', 'Return to Vendor', 'Destroy'],
  DESTROYED: ['Stock', 'Quarantine'],
  PICKED: ['Stock', 'Ship', 'Quarantine'],
  SHIPPED: ['Print Labels'],
  // EPCIS overage: listed by the vendor, not stocked when its PO closed. Nothing to do with it here.
  NOT_RECEIVED: []
};

const showDetailStatus = [
  'CREATED',
  'RECEIVED',
  'STOCKED',
  'QUARANTINED',
  'DESTROYED',
  'PICKED',
  'SHIPPED',
  'NOT_RECEIVED'
];

const ScannedOptions = ({ serializedItemState }) => {
  const [scannedDetailsOpen, setScannedDetailsOpen] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { purchaseOrder, salesOrder, zone, status, serialNumber, gtin } =
    serializedItemState || {};
  const { id: purchaseOrderId, clientPurchaseOrderNumber } = purchaseOrder || {};
  const {
    id: salesOrderId,
    vendorSalesOrderNumber: salesOrderNumber,
    client
  } = salesOrder || {};
  const { companyName: clientName } = client || {};
  const { name: zoneName } = zone || {};

  // The scanned barcode's lot / expiration ride along so Receive and Stock can send them.
  const handleAction = (actionName) =>
    navigate(
      `/${actionName.toLowerCase()}?serialNumber=${serialNumber}&gtin=${gtin}${scannedLotSearch(
        readScannedLot(searchParams)
      )}`
    );

  // Bulk Stock / Bulk Move open with this item already on the list; the rest are scanned there.
  const bulkPath = (path: string) =>
    navigate(
      `${path}?serialNumber=${encodeURIComponent(serialNumber || '')}${
        gtin ? `&gtin=${encodeURIComponent(gtin)}` : ''
      }${scannedLotSearch(readScannedLot(searchParams))}`
    );

  const actionToHandlerMap = {
    'Bulk Stock': () => bulkPath('/bulk-stock'),
    'Bulk Move': () => bulkPath('/bulk-move'),
    Ship: () => navigate(`/sales-order-summary/${salesOrderId}`),
    'Print Labels': () => navigate(`/sales-order-documents/${salesOrderId}`),
    // One scan identified the PO. The receive screen checks off each product from there.
    Receive: () => {
      if (!purchaseOrderId) {
        alert(
          'No purchase order',
          'This serial is not tied to a purchase order.',
          6,
          'error'
        );
        return;
      }
      const gtinQuery = gtin ? `&gtin=${encodeURIComponent(gtin)}` : '';
      navigate(
        `/purchase-order-receive/${purchaseOrderId}?serialNumber=${encodeURIComponent(
          serialNumber || ''
        )}${gtinQuery}`
      );
    }
  };

  return (
    <Page className={['scanner-options', status.split('_').join('-')].join(' ')}>
      <div className="top">
        <Button
          type="secondary"
          iconBefore={<FiChevronLeft />}
          onClick={() => navigate('/')}
        >
          Back Home
        </Button>
        <div className="status">
          <span>Status:</span> <span>{status.split('_').join(' ')}</span>
        </div>
      </div>

      {!!clientPurchaseOrderNumber && (
        <div className="scanned-text">
          <span>PO Number:</span>
          <span>{clientPurchaseOrderNumber}</span>
        </div>
      )}

      {/* A unit found loose after fulfillment: which order, and whose, it left on. */}
      {status === 'SHIPPED' && !!salesOrderNumber && (
        <div className="scanned-text">
          <span>SO Number:</span>
          <span>{salesOrderNumber}</span>
        </div>
      )}
      {status === 'SHIPPED' && !!clientName && (
        <div className="scanned-text">
          <span>Client:</span>
          <span>{clientName}</span>
        </div>
      )}

      {/* Where the records shelve it, so a unit found in the wrong zone can be put back. */}
      {status === 'STOCKED' && !!zoneName && (
        <div className="scanned-text">
          <span>Zone:</span>
          <span>{zoneName}</span>
        </div>
      )}

      <div className="serial-details">
        {!!serialNumber && (
          <div className="scanned-text">
            <span>Serial:</span>
            <span>{serialNumber}</span>
          </div>
        )}
        {showDetailStatus.includes(status) && (
          <div className="open-details" onClick={() => setScannedDetailsOpen(true)}>
            <FiEye />
          </div>
        )}
      </div>

      {status === 'NOT_RECEIVED' ? (
        <div className="select-text">
          Marked not received when its purchase order closed: the vendor's EPCIS listed more units
          than were ordered, and this one was not stocked. Set it aside and report it.
        </div>
      ) : (
        <div className="select-text">Select an Action:</div>
      )}
      {(statusToActionNamesMap[status] || []).map((actionName, i) => (
        <Button
          index={i}
          className="action"
          type="secondary"
          onClick={() =>
            actionToHandlerMap[actionName]
              ? actionToHandlerMap[actionName]()
              : handleAction(actionName)
          }
          fullWidth
          iconAfter={<FiChevronRight />}
        >
          {actionName}
        </Button>
      ))}
      {scannedDetailsOpen && (
        <ScannedDetails
          serialNumber={serialNumber}
          gtin={gtin}
          status={status}
          zone={zone}
          salesOrder={salesOrder}
          close={() => setScannedDetailsOpen(false)}
        />
      )}
    </Page>
  );
};

export default ScannedOptions;
